import { useState, ReactNode } from 'react';
import { ArrowLeftRight, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  useTransactionDetailQuery,
  useTransactionListQuery,
} from '@/client/api/payment/payment.query';
import { useSetTransactionFulfilledMutation } from '@/client/api/payment/payment.mutation';
import {
  formatMoney,
  LedgerTransaction,
  TransactionListParams,
} from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { PaymentsFilterBar, SortableTh } from '@/client/components/admin/Payment/PaymentsFilterBar';
import {
  EntityRelationshipLink,
  RelationshipLink,
  StoreRelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

import { ExportCsvButton } from '@/client/components/admin/Payment/ExportCsvButton';
const FULFILLED_OPTIONS = [
  { value: '1', label: 'Fulfilled' },
  { value: '0', label: 'Unfulfilled' },
];

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const isFulfilled = (v: number | boolean | undefined) => v === 1 || v === true;

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing transactions requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const TransactionDetailsDrawer = ({
  tid,
  isOpen,
  setIsOpen,
  canEdit,
  onClose,
}: {
  tid: number | null;
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  canEdit: boolean;
  onClose: () => void;
}) => {
  const { data: txn, isLoading } = useTransactionDetailQuery(isOpen ? tid : null);
  const setFulfilled = useSetTransactionFulfilledMutation();

  if (!isOpen) return null;

  const fulfilled = isFulfilled(txn?.fulfilled);
  const entitySummary = txn?.entity_summary;

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-blue-600" />
            {isLoading ? 'Loading…' : `Transaction #${txn?.tid ?? tid}`}
          </DrawerTitle>
          <DrawerDescription>
            {txn?.ttype ? String(txn.ttype) : 'Purchase ledger line (audit)'}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {!isLoading && txn && (
            <>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                  fulfilled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {fulfilled ? 'FULFILLED' : 'UNFULFILLED'}
              </span>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatMoney(txn.amount, txn.ccode)}
                </p>
                {txn.amount_less != null && (
                  <p className="mt-1 text-sm text-gray-500">
                    Net: {formatMoney(txn.amount_less, txn.ccode)}
                    {txn.tax != null ? ` · Tax: ${formatMoney(txn.tax, txn.ccode)}` : ''}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="TID" value={txn.tid} />
                <InfoRow label="Type" value={txn.ttype || (txn.ttid != null ? `ttid ${txn.ttid}` : '—')} />
                <InfoRow label="User (from)" value={<UserRelationshipLink uid={txn.uid_from} summary={txn.user_summary} />} />
                <InfoRow label="UID to" value={txn.uid_to ?? '—'} />
                <InfoRow label="Beneficiary" value={txn.uid_beneficiary ?? '—'} />
                <InfoRow label="Entity" value={<EntityRelationshipLink eid={txn.eid} summary={entitySummary} />} />
                <InfoRow
                  label="Invoice"
                  value={txn.invid != null ? <RelationshipLink kind="invoice" id={txn.invid} /> : '—'}
                />
                <InfoRow
                  label="Payment"
                  value={txn.pid != null ? <RelationshipLink kind="payment" id={txn.pid} /> : '—'}
                />
                <InfoRow
                  label="Store"
                  value={<StoreRelationshipLink storeid={txn.storeid} summary={txn.store_summary} />}
                />
                <InfoRow label="TO (toid)" value={txn.toid ?? '—'} />
                <InfoRow label="Quantity" value={txn.quantity ?? '—'} />
                <InfoRow label="Added" value={formatDate(txn.date_added)} />
              </div>
              <p className="rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                This flag is an audit correction only. To provision a purchase, fulfill the linked
                payment instead.
              </p>
            </>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canEdit && txn && (
              <Button
                type="button"
                size="sm"
                variant={fulfilled ? 'outline' : 'default'}
                disabled={setFulfilled.isPending}
                onClick={async () => {
                  try {
                    await setFulfilled.mutateAsync({
                      tid: txn.tid,
                      fulfilled: fulfilled ? 0 : 1,
                    });
                    toast.success(
                      fulfilled ? 'Marked unfulfilled' : 'Marked fulfilled',
                      { richColors: true },
                    );
                  } catch (error: any) {
                    toast.error(error?.message || 'Failed to update fulfilled flag', {
                      richColors: true,
                    });
                  }
                }}
              >
                {setFulfilled.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />
                )}
                {fulfilled ? 'Mark unfulfilled' : 'Mark fulfilled'}
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const TransactionListPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Transactions' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const editPerm = useHasPermission('payment.edit');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { state, offset, patch, clearFilters, setDetail, toggleSort, toApiNumber } =
    usePaymentListUrlState();

  const listParams: TransactionListParams = {
    limit: state.limit,
    offset,
    ...(state.q.trim() ? { q: state.q.trim() } : {}),
    ...(state.fulfilled === '0' || state.fulfilled === '1'
      ? { fulfilled: Number(state.fulfilled) as 0 | 1 }
      : {}),
    ...(state.uid ? { uid: toApiNumber(state.uid) } : {}),
    ...(state.oid ? { oid: toApiNumber(state.oid) } : {}),
    ...(state.eid ? { eid: toApiNumber(state.eid) } : {}),
    ...(state.storeid ? { storeid: toApiNumber(state.storeid) } : {}),
    ...(state.ccode ? { ccode: state.ccode } : {}),
    ...(state.date_from ? { date_from: state.date_from } : {}),
    ...(state.date_to ? { date_to: state.date_to } : {}),
    ...(state.amount_min ? { amount_min: toApiNumber(state.amount_min) } : {}),
    ...(state.amount_max ? { amount_max: toApiNumber(state.amount_max) } : {}),
    ...(state.sort_by ? { sort_by: state.sort_by, order: state.order || 'desc' } : {}),
  };

  const { data, isLoading, isError, error } = useTransactionListQuery(
    listParams,
    canView && !permLoading,
  );

  const transactions = data?.transactions ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <ArrowLeftRight className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Transactions</h1>
          </div>
          <p className="text-sm text-gray-500">
            Purchase ledger audit — correct the fulfilled flag when needed.
          </p>
          <div className="mt-3">
            <ExportCsvButton path="/api/admin/v2/transactions" params={listParams} filename="transactions.csv" />
          </div>
        </div>

        <PaymentsFilterBar
          state={state}
          onPatch={patch}
          onClear={() => clearFilters()}
          fulfilledOptions={FULFILLED_OPTIONS}
          showAmount
          showDates
          showOid
          showCcode
          searchPlaceholder="Search by uid, eid, or id…"
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load transactions'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    TID
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Type
                  </th>
                  <SortableTh
                    label="Amount"
                    field="amount"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Fulfilled
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    User
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Entity
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Invoice
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Payment
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Store
                  </th>
                  <SortableTh
                    label="Date"
                    field="date_added"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-16" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && transactions.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-16 text-center text-sm text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  transactions.map((txn: LedgerTransaction) => {
                    const ok = isFulfilled(txn.fulfilled);
                    return (
                      <tr
                        key={txn.tid}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setDetail('tid', txn.tid)}
                      >
                        <td className="px-6 py-4 font-mono text-sm font-semibold">{txn.tid}</td>
                        <td className="px-6 py-4 text-sm">{txn.ttype || '—'}</td>
                        <td className="px-6 py-4 text-sm font-bold">
                          {formatMoney(txn.amount, txn.ccode)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              ok
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {ok ? 'YES' : 'NO'}
                          </span>
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <UserRelationshipLink uid={txn.uid_from} summary={txn.user_summary} />
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <EntityRelationshipLink eid={txn.eid} summary={txn.entity_summary} />
                        </td>
                        <td className="px-6 py-4">
                          {txn.invid != null ? (
                            <RelationshipLink kind="invoice" id={txn.invid} />
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {txn.pid != null ? (
                            <RelationshipLink kind="payment" id={txn.pid} />
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="max-w-[140px] px-6 py-4">
                          <StoreRelationshipLink storeid={txn.storeid} summary={txn.store_summary} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(txn.date_added)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={(page) => patch({ page }, false)}
              setItemsPerPage={(limit) => patch({ limit, page: 1 }, false)}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <TransactionDetailsDrawer
        tid={state.tid}
        isOpen={state.tid != null}
        setIsOpen={(open) => {
          if (!open) setDetail('tid', null);
        }}
        canEdit={canEdit}
        onClose={() => setDetail('tid', null)}
      />
    </div>
  );
};

export default TransactionListPage;
